import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Divider,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { extractApiErrorMessage } from '../../../../utils/ErrorUtils';
import {
  IBaixaDados,
  IEstatuto,
  IEstatutoAssinaturaResponse,
  useEstatuto,
} from '../../../../hooks/useEstatuto';

interface EstatutoAssinaturaModalProps {
  isOpen: boolean;
  onClose: () => void;
  estatuto: IEstatuto;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EstatutoAssinaturaModal: React.FC<EstatutoAssinaturaModalProps> = ({
  isOpen,
  onClose,
  estatuto,
}) => {
  const queryClient = useQueryClient();
  const { validarEstatuto, enviarAssinatura } = useEstatuto();

  const dados = estatuto.dados_preenchidos;
  // Quem o admin pode corrigir: na Baixa, o presidente (2ª parte); na constituição, o
  // signatário (Presidente). O back aplica a correção no mesmo lugar.
  const baixa = (dados ?? {}) as IBaixaDados;
  let principal = {
    rotulo: 'signatário',
    nome: dados?.signatario_nome,
    email: dados?.signatario_email,
    cpf: dados?.signatario_cpf,
  };
  if (estatuto.destino === 'baixa' && baixa.assembleia)
    principal = {
      rotulo: 'presidente (assina o requerimento)',
      nome: baixa.assembleia.presidente_nome,
      email: baixa.assembleia.presidente_email,
      cpf: baixa.assembleia.presidente_cpf,
    };

  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [nomeErr, setNomeErr] = useState<string | null>(null);
  const [emailErr, setEmailErr] = useState<string | null>(null);
  // Advogado (2º assinante) — pré-preenchido com o que veio da criação.
  const [advNome, setAdvNome] = useState('');
  const [advEmail, setAdvEmail] = useState('');
  const [advCpf, setAdvCpf] = useState('');
  const [advOab, setAdvOab] = useState('');
  const [advNomeErr, setAdvNomeErr] = useState<string | null>(null);
  const [advEmailErr, setAdvEmailErr] = useState<string | null>(null);
  const [advCpfErr, setAdvCpfErr] = useState<string | null>(null);
  const [advOabErr, setAdvOabErr] = useState<string | null>(null);
  const [result, setResult] = useState<IEstatutoAssinaturaResponse | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNome(principal.nome || '');
      setEmail(principal.email || '');
      setAdvNome(estatuto.advogado_nome || '');
      setAdvEmail(estatuto.advogado_email || '');
      setAdvCpf(estatuto.advogado_cpf || '');
      setAdvOab(estatuto.advogado_oab || '');
      setNomeErr(null);
      setEmailErr(null);
      setAdvNomeErr(null);
      setAdvEmailErr(null);
      setAdvCpfErr(null);
      setAdvOabErr(null);
      setResult(null);
    }
  }, [isOpen, dados, estatuto]);

  const mutation = useMutation({
    mutationFn: async () => {
      // Se ainda está em validação, valida antes de enviar pra assinatura.
      if (estatuto.status === 'aguardando_validacao') {
        await validarEstatuto(estatuto.id);
      }
      return enviarAssinatura(estatuto.id, {
        signatario_nome: nome.trim(),
        signatario_email: email.trim(),
        advogado_nome: advNome.trim() || undefined,
        advogado_email: advEmail.trim() || undefined,
        advogado_cpf: advCpf.trim() || undefined,
        advogado_oab: advOab.trim() || undefined,
      });
    },
    onSuccess: res => {
      setResult(res.data);
      toast.success(res.message || 'Documentos enviados para assinatura');
      queryClient.invalidateQueries({ queryKey: ['estatutos'] });
      queryClient.invalidateQueries({ queryKey: ['kanban-board'] });
      queryClient.invalidateQueries({ queryKey: ['estatuto', estatuto.id] });
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      // 400 (validação de dados) e 409 (já existe documento ativo) trazem
      // mensagem útil do backend — surfacia o detalhe.
      if ((status === 400 || status === 409) && detail) {
        // A validação do corpo (422/400) vem em lista: o toast não renderiza objeto.
        toast.error(extractApiErrorMessage(err));
      } else if (status === 400) {
        toast.error('Dados inválidos — verifique nome e email');
      } else {
        toast.error('Erro ao enviar para assinatura');
      }
    },
  });

  const handleSubmit = () => {
    let hasErr = false;
    if (!nome.trim()) {
      setNomeErr('Nome obrigatório');
      hasErr = true;
    } else {
      setNomeErr(null);
    }
    if (!email.trim()) {
      setEmailErr('Email obrigatório');
      hasErr = true;
    } else if (!EMAIL_REGEX.test(email.trim())) {
      setEmailErr('Email inválido');
      hasErr = true;
    } else {
      setEmailErr(null);
    }
    // Advogado (2º assinante) — o back exige nome e e-mail para o Estatuto e as Atas.
    // Só valida FORMATO se preenchido (email válido); nome/cpf/oab vazios são aceitos
    // (o backend já recebe undefined — o envio faz `.trim() || undefined`).
    setAdvNomeErr(null);
    if (advEmail.trim() && !EMAIL_REGEX.test(advEmail.trim())) {
      setAdvEmailErr('Email inválido');
      hasErr = true;
    } else {
      setAdvEmailErr(null);
    }
    setAdvCpfErr(null);
    setAdvOabErr(null);
    if (hasErr) return;
    mutation.mutate();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay bg="blackAlpha.500" />
      <ModalContent borderRadius="xl">
        <ModalHeader fontSize="md" fontWeight="700" color="gray.800">
          {result ? 'Enviado para assinatura' : 'Enviar para assinatura'}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {result ? (
            <Flex direction="column" gap={3}>
              <Text fontSize="sm" color="gray.600">
                Os documentos estão indo para a Autentique em segundo plano (pode levar alguns
                minutos). Cada assinante recebe o convite por e-mail; o andamento e os links ficam
                na aba Assinatura do card.
              </Text>
              {result.documentos.map(d => (
                <Flex
                  key={d.nome}
                  justify="space-between"
                  gap={2}
                  bg="gray.50"
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="lg"
                  px={3}
                  py={2}
                >
                  <Text fontSize="xs" color="gray.700" fontWeight="600" noOfLines={2}>
                    {d.nome}
                  </Text>
                  <Text fontSize="xs" color="gray.500" flexShrink={0}>
                    {d.assinantes} assinante(s)
                  </Text>
                </Flex>
              ))}
            </Flex>
          ) : (
            <Flex direction="column" gap={4}>
              <Text fontSize="sm" color="gray.600">
                Confira os dados do signatário antes de enviar para a Autentique. Caso o cliente
                tenha digitado o email incorretamente, você pode corrigir aqui.
              </Text>

              <FormControl isRequired isInvalid={!!nomeErr}>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  Nome de {principal.rotulo}
                </FormLabel>
                <Input value={nome} onChange={e => setNome(e.target.value)} />
                {nomeErr && <FormErrorMessage>{nomeErr}</FormErrorMessage>}
              </FormControl>

              <FormControl isRequired isInvalid={!!emailErr}>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  E-mail de {principal.rotulo}
                </FormLabel>
                <Input type="email" value={email} onChange={e => setEmail(e.target.value)} />
                {emailErr && <FormErrorMessage>{emailErr}</FormErrorMessage>}
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600" color="gray.700">
                  CPF
                </FormLabel>
                <Input value={principal.cpf || ''} isReadOnly bg="gray.50" />
                <Text fontSize="11px" color="gray.500" mt={1}>
                  CPF já foi validado no preenchimento e não pode ser alterado aqui.
                </Text>
              </FormControl>

              <Divider />

              <Box>
                <Text fontSize="sm" fontWeight="700" color="gray.700" mb={1}>
                  Advogado (2º assinante)
                </Text>
                <Text fontSize="11px" color="gray.500" mb={3}>
                  Assina o Estatuto e as Atas: sem nome e e-mail, o envio é recusado enquanto um
                  deles faltar. CPF/OAB são apenas registro interno.
                </Text>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                  <FormControl isInvalid={!!advNomeErr}>
                    <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                      Nome
                    </FormLabel>
                    <Input
                      size="sm"
                      value={advNome}
                      onChange={e => {
                        setAdvNome(e.target.value);
                        if (e.target.value.trim()) setAdvNomeErr(null);
                      }}
                    />
                    {advNomeErr && <FormErrorMessage>{advNomeErr}</FormErrorMessage>}
                  </FormControl>
                  <FormControl isInvalid={!!advEmailErr}>
                    <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                      Email
                    </FormLabel>
                    <Input
                      size="sm"
                      type="email"
                      value={advEmail}
                      onChange={e => {
                        setAdvEmail(e.target.value);
                        if (e.target.value.trim()) setAdvEmailErr(null);
                      }}
                    />
                    {advEmailErr && <FormErrorMessage>{advEmailErr}</FormErrorMessage>}
                  </FormControl>
                  <FormControl isInvalid={!!advCpfErr}>
                    <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                      CPF
                    </FormLabel>
                    <Input
                      size="sm"
                      value={advCpf}
                      onChange={e => {
                        setAdvCpf(e.target.value);
                        if (e.target.value.trim()) setAdvCpfErr(null);
                      }}
                    />
                    {advCpfErr && <FormErrorMessage>{advCpfErr}</FormErrorMessage>}
                  </FormControl>
                  <FormControl isInvalid={!!advOabErr}>
                    <FormLabel fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                      OAB
                    </FormLabel>
                    <Input
                      size="sm"
                      value={advOab}
                      onChange={e => {
                        setAdvOab(e.target.value);
                        if (e.target.value.trim()) setAdvOabErr(null);
                      }}
                    />
                    {advOabErr && <FormErrorMessage>{advOabErr}</FormErrorMessage>}
                  </FormControl>
                </SimpleGrid>
              </Box>
            </Flex>
          )}
        </ModalBody>
        <ModalFooter gap={2}>
          {result ? (
            <Button colorScheme="blue" onClick={onClose}>
              Fechar
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose}>
                Cancelar
              </Button>
              <Button colorScheme="blue" onClick={handleSubmit} isLoading={mutation.isPending}>
                Confirmar e enviar
              </Button>
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
